// src/utils/documentTimeEstimation.js

// Constants for time estimation
const AVERAGE_READING_SPEED = 200; // words per minute
const MINIMUM_READING_TIME = 0.1; // 6 minutes minimum
const MINIMUM_WRITING_TIME = 0.25; // 15 minutes minimum
const MINIMUM_REVIEW_TIME = 0.25; // 15 minutes minimum

// Document type multipliers for writing time
const DOCUMENT_TYPE_MULTIPLIERS = {
  simple: 1.0,
  user_guide: 1.3,
  technical: 1.8,
  policy: 1.5,
  specification: 2.0,
  research: 2.2
};

// Complexity multipliers
const COMPLEXITY_MULTIPLIERS = {
  low: 0.8,
  medium: 1.0,
  high: 1.4
};

// Review thoroughness multipliers
const REVIEW_THOROUGHNESS = {
  light: 0.3,
  standard: 0.4,
  thorough: 0.6
};

// Technical keywords that indicate complexity
const TECHNICAL_KEYWORDS = [
  'api', 'algorithm', 'architecture', 'authentication', 'database', 'implementation',
  'integration', 'specification', 'configuration', 'deployment', 'security', 'protocol',
  'framework', 'methodology', 'infrastructure', 'optimization', 'performance'
];

/**
 * Estimates reading time based on content length
 * @param {string|number} content - Content string or word count
 * @returns {number} Reading time in hours
 */
export function estimateReadingTime(content) {
  if (!content) return 0;
  
  const wordCount = parseWordCount(content);
  if (wordCount === 0) return 0;
  
  const readingTimeMinutes = wordCount / AVERAGE_READING_SPEED;
  
  // For very short content, use minimum time
  if (readingTimeMinutes < 6) return MINIMUM_READING_TIME;
  
  return readingTimeMinutes / 60; // Convert to hours
}

/**
 * Estimates writing time based on content length, type, and complexity
 * @param {string|number} content - Content string or word count
 * @param {string} documentType - Type of document
 * @param {string} complexity - Complexity level
 * @returns {number} Writing time in hours
 */
export function estimateWritingTime(content, documentType = 'simple', complexity = null) {
  if (!content) return MINIMUM_WRITING_TIME;
  
  const wordCount = parseWordCount(content);
  if (wordCount === 0) return MINIMUM_WRITING_TIME;
  
  // Base writing time: assume 15 words per minute for initial drafting (slower than reading)
  const baseWritingMinutes = wordCount / 15;
  const baseWritingHours = baseWritingMinutes / 60;
  
  // Apply document type multiplier
  const typeMultiplier = DOCUMENT_TYPE_MULTIPLIERS[documentType] || 1.0;
  
  // Determine complexity if not provided
  const detectedComplexity = complexity || getDocumentComplexity(content);
  const complexityMultiplier = COMPLEXITY_MULTIPLIERS[detectedComplexity] || 1.0;
  
  const totalWritingTime = baseWritingHours * typeMultiplier * complexityMultiplier;
  
  return Math.max(totalWritingTime, MINIMUM_WRITING_TIME);
}

/**
 * Estimates review time as a percentage of writing time
 * @param {number} writingTimeHours - Time spent writing
 * @param {string} complexity - Document complexity
 * @param {string} thoroughness - Review thoroughness level
 * @returns {number} Review time in hours
 */
export function estimateReviewTime(writingTimeHours, complexity = 'medium', thoroughness = 'standard') {
  if (writingTimeHours === 0) return 0;
  
  const complexityMultiplier = COMPLEXITY_MULTIPLIERS[complexity] || 1.0;
  const thoroughnessMultiplier = REVIEW_THOROUGHNESS[thoroughness] || 0.4;
  
  const reviewTime = writingTimeHours * thoroughnessMultiplier * complexityMultiplier;
  
  return Math.max(reviewTime, MINIMUM_REVIEW_TIME);
}

/**
 * Determines document complexity based on content characteristics
 * @param {string} content - Document content
 * @returns {string} Complexity level: 'low', 'medium', or 'high'
 */
export function getDocumentComplexity(content) {
  if (typeof content !== 'string') return 'low';
  
  const contentLower = content.toLowerCase();
  const wordCount = parseWordCount(content);
  
  // Count technical keywords
  const technicalKeywordCount = TECHNICAL_KEYWORDS.filter(keyword => 
    contentLower.includes(keyword)
  ).length;
  
  // Analyze sentence structure (average sentence length)
  const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const avgSentenceLength = wordCount / Math.max(sentences.length, 1);
  
  // Scoring system
  let complexityScore = 0;
  
  // Length factor
  if (wordCount > 1000) complexityScore += 2;
  else if (wordCount > 200) complexityScore += 1;
  
  // Technical keywords factor
  if (technicalKeywordCount > 3) complexityScore += 2;
  else if (technicalKeywordCount > 1) complexityScore += 1;
  
  // Sentence complexity factor
  if (avgSentenceLength > 25) complexityScore += 2;
  else if (avgSentenceLength > 18) complexityScore += 1;
  
  // Determine complexity level
  if (complexityScore >= 3) return 'high';
  if (complexityScore >= 1) return 'medium';
  return 'low';
}

/**
 * Parses word count from content string or returns number directly
 * @param {string|number} content - Content string or word count number
 * @returns {number} Word count
 */
export function parseWordCount(content) {
  if (typeof content === 'number') {
    return content;
  }
  
  if (typeof content !== 'string' || !content.trim()) {
    return 0;
  }
  
  // Handle line breaks properly and remove extra whitespace
  const normalizedContent = content.replace(/\\n/g, '\n').trim();
  
  // Split by whitespace and filter out empty strings
  const words = normalizedContent.split(/\s+/).filter(word => word.length > 0);
  return words.length;
}